import { router, useLocalSearchParams } from 'expo-router';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useCreateBillingInfo, useEditBillingInfo } from '@/api/hooks/individualHooks';
import { BillingInfoResponse } from '@/api/models/individualModels';
import BillingAddressForm from '@/components/forms/BillingAddressForm';
import { useAuth } from '@/contexts/authContext';
import { addressFromString } from '@/utils/addressFromString';
import { decryptObject, encryptObject } from '@/utils/crypto';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';

import { PaymentMethod } from '.';

const BillingAddress = () => {
  const { payload } = useLocalSearchParams<{
    payload: string;
  }>();

  const { mutateAsync: createBillingInfo, isPending: createPending } = useCreateBillingInfo();
  const { mutateAsync: updateBillingInfo, isPending: editPending } = useEditBillingInfo();

  const { state } = useAuth();

  const [payloadState, setPayloadState] = useState<{
    tokenizedCard?: string;
    tokenizedACH?: string;
    donationData: string;
    individualNuveiUser: string;
    paymentMethod: PaymentMethod;
    fromIOS?: string;
    influencerCode?: string;
    routeWithSelectedMethod?: string;
    subscriptionItem?: string;
    showId?: string;
  }>();

  const [parsedIndividualNuveiUser, setParsedIndividualNuveiUser] = useState<BillingInfoResponse>();

  useEffect(() => {
    const loadPayloadParams = async () => {
      const payloadObject = await decryptObject<{
        tokenizedCard?: string;
        tokenizedACH?: string;
        donationData: string;
        individualNuveiUser: string;
        paymentMethod: PaymentMethod;
        fromIOS?: string;
        influencerCode?: string;
        routeWithSelectedMethod?: string;
        subscriptionItem?: string;
        showId?: string;
      }>(decodeURIComponent(payload));

      if (payloadObject) {
        setPayloadState(payloadObject);
        const individualNuveiUserObject = safeJsonParse<BillingInfoResponse>(payloadObject.individualNuveiUser ?? '');
        setParsedIndividualNuveiUser(individualNuveiUserObject);
      }
    };

    if (payload) {
      loadPayloadParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  return (
    <SafeAreaView style={styles.flexOne} edges={['bottom']}>
      <View style={styles.container}>
        <BillingAddressForm
          initialData={{
            firstName: parsedIndividualNuveiUser?.firstName ?? state.user?.user?.firstName ?? '',
            lastName: parsedIndividualNuveiUser?.lastName ?? state.user?.user?.lastName ?? '',
            address1:
              parsedIndividualNuveiUser?.street1 !== undefined &&
              !isEmpty(parsedIndividualNuveiUser?.street1) &&
              parsedIndividualNuveiUser?.city !== undefined &&
              !isEmpty(parsedIndividualNuveiUser?.city) &&
              parsedIndividualNuveiUser?.stateId !== undefined &&
              !isEmpty(parsedIndividualNuveiUser?.stateId) &&
              parsedIndividualNuveiUser?.zipCode !== undefined &&
              !isEmpty(parsedIndividualNuveiUser?.zipCode)
                ? `${parsedIndividualNuveiUser?.street1}, ${parsedIndividualNuveiUser?.city}, ${parsedIndividualNuveiUser?.stateId} ${parsedIndividualNuveiUser?.zipCode}`
                : '',
            // address2: '',
            email: parsedIndividualNuveiUser?.email ?? state.user?.user?.email ?? '',
            emailConfirmation: parsedIndividualNuveiUser?.email ?? state.user?.user?.email ?? ''
          }}
          doneLabel={i18n.t('mix.next')}
          isLoading={createPending || editPending}
          onDone={(data) => {
            const formattedAddress = addressFromString(data.address1);
            const emailValue = data.email.toLocaleLowerCase();

            //Update account then go to payment with selected method
            if (payloadState?.tokenizedCard || payloadState?.tokenizedACH) {
              updateBillingInfo(
                {
                  customer_id: state.user?.user?.id ?? '-',
                  customer_name: `${data.firstName} ${data.lastName}`,
                  first_name: data.firstName,
                  last_name: data.lastName,
                  street1: formattedAddress?.address ?? data.address1 ?? '',
                  city: formattedAddress?.city ?? '',
                  state_id: formattedAddress?.state ?? '',
                  zip_code: formattedAddress?.postal_code ?? '',
                  email: emailValue
                },
                {
                  onSuccess: async (response) => {
                    const newPayload = { ...payloadState, individualNuveiUser: JSON.stringify(response) };

                    const encrypted = await encryptObject(newPayload);

                    if (payloadState?.donationData) {
                      router.replace({
                        pathname: '/(app)/donationPayment',
                        params: {
                          payload: encodeURIComponent(encrypted)
                        }
                      });
                    } else {
                      // Check if param is route
                      router.navigate({
                        pathname: payloadState?.routeWithSelectedMethod as any,
                        params: {
                          payload: encodeURIComponent(encrypted)
                        }
                      });
                    }
                  }
                }
              );
            } else if (payloadState?.individualNuveiUser === undefined) {
              //Create account then add method
              createBillingInfo(
                {
                  customer_id: state.user?.user?.id ?? '-',
                  customer_name: `${data.firstName} ${data.lastName}`,
                  first_name: data.firstName,
                  last_name: data.lastName,
                  street1: formattedAddress?.address ?? data.address1 ?? '',
                  city: formattedAddress?.city ?? '',
                  state_id: formattedAddress?.state ?? '',
                  zip_code: formattedAddress?.postal_code ?? '',
                  email: emailValue
                },
                {
                  onSuccess: async (response) => {
                    const newPayload = {
                      ...payloadState,
                      fromBillingData: 'true',
                      individualNuveiUser: JSON.stringify(response)
                    };

                    const encrypted = await encryptObject(newPayload);

                    switch (payloadState?.paymentMethod) {
                      case 'ach':
                        router.replace({
                          pathname: '/(app)/paymentMethods/addAch',
                          params: {
                            payload: encodeURIComponent(encrypted)
                          }
                        });
                        break;

                      case 'card':
                        router.replace({
                          pathname: '/(app)/paymentMethods/addCreditCard',
                          params: {
                            payload: encodeURIComponent(encrypted)
                          }
                        });
                        break;

                      default:
                        break;
                    }
                  }
                }
              );
            }
          }}
        />
      </View>
    </SafeAreaView>
  );
};

export default BillingAddress;

const styles = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  container: {
    flex: 1,
    alignItems: 'center',
    gap: 10
  },

  errorText: {
    fontSize: 16,
    color: 'red'
  }
});
